import { useFormState } from 'react-hook-form';
import { FormError } from 'shared/components/FormControl';

import Form from 'dashboard/layouts/FormLayout/Form';
import Button from 'design-system/components/Button';
import Input from 'design-system/components/Input';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { Label } from 'design-system/components/Text';
import { useFormSecurely } from 'lib/security/reactHookForm';

interface FormCreateReportBusinessOnlinePresenceProps {
  defaultValues?: FormCreateReportBusinessOnlinePresenceData;
  handleSubmit: (data: FormCreateReportBusinessOnlinePresenceData) => void;
  handleCancel?: () => void;
  readOnly?: boolean;
}

const FormCreateReportBusinessOnlinePresence = ({
  defaultValues,
  handleSubmit,
  handleCancel = () => null,
  readOnly,
}: FormCreateReportBusinessOnlinePresenceProps) => {
  const {
    register,
    handleSubmit: handleReactHookFormSubmit,
    control,
  } = useFormSecurely<FormCreateReportBusinessOnlinePresenceData>({
    mode: 'onBlur',
    defaultValues: defaultValues,
  });

  const { isSubmitting, isDirty, errors } = useFormState({
    control,
  });
  const disabled = isSubmitting || readOnly;

  const submit = handleReactHookFormSubmit((data: FormCreateReportBusinessOnlinePresenceData) => {
    handleSubmit(data);
  });

  const cancel = () => {
    handleCancel();
  };

  return (
    <Form
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          submit();
        }
      }}
    >
      <Label
        htmlFor='form--create-report-business-online-presence__input--business-name'
        size='lg'
        weight='bold'
      >
        Business Name
      </Label>
      <Spacer height={1} />
      <Input
        {...register('business-name', {
          validate: (v) => {
            if (v) {
              return true;
            } else {
              return 'Business name is required';
            }
          },
        })}
        id='form--create-report-business-online-presence__input--business-name'
        type='text'
        placeholder='Business Name'
        disabled={disabled}
      />
      {errors['business-name'] && <FormError>{errors['business-name'].message}</FormError>}
      <Spacer height={1} />
      <Label
        htmlFor='form--create-report-business-online-presence__input--address'
        size='lg'
        weight='bold'
      >
        Address
      </Label>
      <Spacer height={1} />
      <Input
        {...register('address-street-1')}
        id='form--create-report-business-online-presence__input--address-street-1'
        type='text'
        placeholder='Street address'
        disabled={disabled}
      />
      <Spacer height={1} />
      <Input
        {...register('address-street-2')}
        id='form--create-report-business-online-presence__input--address-street-2'
        type='text'
        placeholder='Apt / Suite'
        disabled={disabled}
      />
      <Spacer height={1} />
      <Input
        {...register('address-city')}
        id='form--create-report-business-online-presence__input--address-city'
        type='text'
        placeholder='City'
        disabled={disabled}
      />
      <Spacer height={1} />
      <Input
        {...register('address-subdivision', {
          validate: (v) => {
            if (v) {
              return true;
            } else {
              return 'State is required';
            }
          },
        })}
        id='form--create-report-business-online-presence__input--address-subdivision'
        type='text'
        placeholder='State'
        disabled={disabled}
      />
      {errors['address-subdivision'] && (
        <FormError>{errors['address-subdivision'].message}</FormError>
      )}
      <Spacer height={1} />
      <Input
        {...register('address-postal-code')}
        id='form--create-report-business-online-presence__input--address-postal-code'
        type='text'
        placeholder='Postal code'
        disabled={disabled}
      />
      <Spacer height={1} />
      <Input
        {...register('address-country-code', {
          validate: (v) => {
            if (v) {
              return true;
            } else {
              return 'Country code is required';
            }
          },
        })}
        id='form--create-report-business-online-presence__input--address-country-code'
        type='text'
        placeholder='Country code'
        disabled={disabled}
      />
      {errors['address-country-code'] && (
        <FormError>{errors['address-country-code'].message}</FormError>
      )}
      <Spacer height={1} />
      <Label
        htmlFor='form--create-report-business-online-presence__input--website'
        size='lg'
        weight='bold'
      >
        Website (optional)
      </Label>
      <Input
        {...register('website')}
        id='form--create-report-business-online-presence__input--website'
        type='text'
        placeholder='Website'
        disabled={disabled}
      />
      <Spacer height={4} />
      <HStack equal={true}>
        <Button
          type='button'
          variant='basic'
          onClick={cancel}
          id='form--create-report-business-online-presence__button--cancel'
        >
          Cancel
        </Button>
        <Button
          type='button'
          disabled={disabled || !isDirty}
          onClick={submit}
          id='form--create-report-business-online-presence__button--submit'
        >
          Create Report
        </Button>
      </HStack>
    </Form>
  );
};

export interface FormCreateReportBusinessOnlinePresenceData {
  'business-name'?: string;
  'address-street-1'?: string;
  'address-street-2'?: string;
  'address-city'?: string;
  'address-subdivision'?: string;
  'address-postal-code'?: string;
  'address-country-code'?: string;
  website?: string;
}

export const FORM_CREATE_REPORT_BUSINESS_ONLINE_PRESENCE_NAME =
  'formCreateReportBusinessOnlinePresenceData';

export default FormCreateReportBusinessOnlinePresence;
